import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { Crm } from '@/services/CrmService';

/* ---------------- Helpers para formatação amigável ---------------- */

const LABELS_MAP: Record<string, string> = {
  perfil_cliente: 'Perfil do Cliente',
  ganchos_conteudo: 'Ganchos de Conteúdo',
  briefing_marketing: 'Briefing de Marketing',
  ocasioes_relevantes: 'Ocasiões Relevantes',
  momento_compra_atual: 'Momento de Compra Atual',
  ocasião: 'Ocasião',
  sugestao_look: 'Sugestão de Look',
  persona_na_ocasião: 'Persona na Ocasião',
  descricao_geral: 'Descrição Geral',
  direcao_visual: 'Direção Visual',
  orientacao_campanhas: 'Orientação de Campanhas',
  navegacoes: 'Navegações',
  relacao_com_historico: 'Relação com Histórico',
  resumo_momento: 'Resumo do Momento',
};

const ORDERED_KEYS = [
  'perfil_cliente',
  'ganchos_conteudo',
  'briefing_marketing',
  'ocasioes_relevantes',
  'momento_compra_atual',
];

function prettyLabel(key: string) {
  if (LABELS_MAP[key]) return LABELS_MAP[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isPlainObject(v: unknown): v is Record<string, any> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Render genérico com tratamento especial quando a chave for tamanhos */
function RenderValue({ value, ctxKey }: { value: any; ctxKey?: string }) {
  const isSizeKey = (k?: string) => {
    const kk = (k ?? '').toLowerCase();
    return (
      kk === 'tamanhos' ||
      kk === 'tamanhos_comprados' ||
      kk === 'sizes' ||
      kk === 'sizes_purchased'
    );
  };

  if (value == null || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }

  // primitivos
  if (typeof value !== 'object') {
    return (
      <p className="max-w-prose text-sm font-light leading-relaxed">
        {String(value)}
      </p>
    );
  }

  // arrays
  if (Array.isArray(value)) {
    // array simples (string/number/bool)
    if (value.every((v) => typeof v !== 'object' || v === null)) {
      // chips lado a lado somente para tamanhos
      if (isSizeKey(ctxKey)) {
        return (
          <div className="flex flex-wrap gap-2">
            {value.map((item, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs"
              >
                {String(item)}
              </span>
            ))}
          </div>
        );
      }

      // padrão: lista vertical
      return (
        <ul className="list-disc space-y-1 pl-5">
          {value.map((item, i) => (
            <li key={i} className="text-sm font-light leading-relaxed">
              {String(item)}
            </li>
          ))}
        </ul>
      );
    }

    // array de objetos
    return (
      <div className="space-y-4">
        {value.map((obj, i) => (
          <div key={i} className="rounded-lg border p-3">
            {isPlainObject(obj) ? (
              <div className="space-y-2">
                {Object.entries(obj).map(([k, v]) => (
                  <div key={k} className="text-sm">
                    <div className="mb-1 font-medium">{prettyLabel(k)}</div>
                    <div className="rounded-md bg-muted/40 p-3">
                      <RenderValue value={v} ctxKey={k} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <RenderValue value={obj} />
            )}
          </div>
        ))}
      </div>
    );
  }

  // objeto plano
  if (isPlainObject(value)) {
    return (
      <div className="grid gap-3">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="text-sm">
            <div className="mb-1 font-medium">{prettyLabel(k)}</div>
            <div className="rounded-md bg-muted/40 p-3">
              <RenderValue value={v} ctxKey={k} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // fallback
  return (
    <pre className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 font-mono text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

/* ---------------- Componente ---------------- */

interface InsightsProps {
  crm: Pick<Crm, 'profile'>;
}

export function Insights({ crm }: InsightsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Profiles.components.ProfileModal',
  });

  // 🔹 Pega o objeto aninhado: crm.profile.profile
  const profileSections = crm?.profile?.profile ?? {};

  // 🔹 Ordena por ORDERED_KEYS e depois adiciona o resto
  const orderedEntries = [
    ...ORDERED_KEYS.filter((k) => k in profileSections).map(
      (k) => [k, profileSections[k]] as const,
    ),
    ...Object.entries(profileSections).filter(
      ([k]) => !ORDERED_KEYS.includes(k),
    ),
  ];

  return (
    <div>
      <div className="px-5 pb-[10px]">
        <h4 className="text-sm font-semibold uppercase">{t('insights')}</h4>
      </div>

      {/* 🔸 Agora cada chave de crm.profile.profile vira um item do Accordion */}
      <Accordion type="single" collapsible>
        {orderedEntries.map(([key, value]) => (
          <AccordionItem key={key} value={key} className="border-none">
            <AccordionTrigger className="border-b border-t p-5 text-sm font-light">
              {prettyLabel(key)}
            </AccordionTrigger>

            <AccordionContent>
              <div className="p-5">
                <RenderValue value={value} ctxKey={key} />
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
